// aifab - AdminPage v2
// Three-tab admin: Overview, Users, Project Access
import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../hooks/apiFetch';
import { Spinner, Modal, Err } from '../components/common';

// ── Design tokens ─────────────────────────────────────────────────────────────
const ROLE_META = {
  ADMIN:     { label:'Platform Admin', color:'#fb923c', bg:'rgba(232,71,10,.1)',  bd:'rgba(232,71,10,.25)',  desc:'Full platform access across all organisations.' },
  ORG_ADMIN: { label:'Org Admin',      color:'#fbbf24', bg:'rgba(251,191,36,.1)', bd:'rgba(251,191,36,.25)', desc:'Manages users and projects within this organisation.' },
  ML_ENGINEER:   { label:'ML Engineer',   color:'#34d399', bg:'rgba(52,211,153,.1)', bd:'rgba(52,211,153,.25)', desc:'Trains models and runs experiments. Full access to runs, models, plugins and servers.' },
  DATA_ENGINEER: { label:'Data Engineer', color:'#60a5fa', bg:'rgba(96,165,250,.1)',  bd:'rgba(96,165,250,.25)', desc:'Manages datasources, feature configs and profiling. Cannot start training runs.' },
  VIEWER:        { label:'Viewer',        color:'#475569', bg:'rgba(107,114,148,.1)', bd:'rgba(107,114,148,.2)', desc:'Read-only access to projects and results.' },
};

const PROJECT_ROLE_META = {
  ADMIN:    { label:'Project Admin',    color:'#fb923c', desc:'Full access. Can add and remove project members.' },
  ML_ENGINEER:   { label:'ML Engineer',   color:'#34d399', desc:'Trains models, starts runs, manages plugins and servers.' },
  DATA_ENGINEER: { label:'Data Engineer', color:'#60a5fa', desc:'Manages datasources and feature configs. Cannot start runs.' },
  VIEWER:   { label:'Viewer',           color:'#475569', desc:'Read-only. Can see runs and metrics.' },
};

const RolePill = ({ code, size = 'sm' }) => {
  const m = ROLE_META[code] || PROJECT_ROLE_META[code] || { label: code, color:'#475569', bg:'rgba(107,114,148,.1)', bd:'rgba(107,114,148,.2)' };
  return (
    <span style={{
      display:'inline-block', fontSize: size === 'sm' ? 10 : 11,
      fontWeight:700, padding: size === 'sm' ? '2px 8px' : '3px 10px',
      borderRadius:3, fontFamily:'var(--f-mono)', letterSpacing:'.04em',
      background: m.bg || 'rgba(107,114,148,.1)',
      color: m.color, border:`1px solid ${m.bd || 'rgba(107,114,148,.2)'}`,
    }}>{m.label}</span>
  );
};

// ── Shared form field ─────────────────────────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div>
    <div style={{ fontSize:11, fontWeight:600, color:'#64748B', marginBottom:4,
      textTransform:'uppercase', letterSpacing:'.07em' }}>{label}</div>
    {children}
    {hint && <div style={{ fontSize:10.5, color:'#94A3B8', marginTop:4 }}>{hint}</div>}
  </div>
);

const Input = ({ value, onChange, type='text', placeholder, autoFocus }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder}
    autoFocus={autoFocus}
    style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px',
      background:'#F7F9FC', border:'1px solid var(--bd2)', borderRadius:4,
      color:'#0F172A', fontFamily:'var(--f-ui)', fontSize:13, outline:'none' }}
    onFocus={e => e.target.style.borderColor='var(--or)'}
    onBlur={e  => e.target.style.borderColor='#B0C0D8'}
  />
);

const Select = ({ value, onChange, children }) => (
  <select value={value} onChange={onChange}
    style={{ width:'100%', padding:'8px 10px', background:'#F7F9FC',
      border:'1px solid var(--bd2)', borderRadius:4,
      color:'#0F172A', fontFamily:'var(--f-ui)', fontSize:13, outline:'none',
      cursor:'pointer' }}>
    {children}
  </select>
);

// ── Create User Modal ─────────────────────────────────────────────────────────
function CreateUserModal({ onClose, onCreated, currentUserRole }) {
  const [form, setForm] = useState({ email:'', full_name:'', password:'', role_code:'ML_ENGINEER' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm(p => ({...p, [k]: e.target.value}));

  const save = async () => {
    if (!form.email || !form.full_name || !form.password) {
      setErr('All fields are required.'); return;
    }
    setSaving(true); setErr(null);
    try {
      await apiFetch('/admin/users', { method:'POST', body: JSON.stringify(form) });
      onCreated(); onClose();
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const availableRoles = currentUserRole === 'ADMIN'
    ? ['ADMIN','ORG_ADMIN','ML_ENGINEER','DATA_ENGINEER','VIEWER']
    : ['ORG_ADMIN','ML_ENGINEER','DATA_ENGINEER','VIEWER'];

  const selectedRole = ROLE_META[form.role_code];

  return (
    <Modal title="Add User" onClose={onClose}>
      <div style={{ width:520 }}>
        <Err msg={err}/>

        {/* Two-column layout */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          <Field label="Full name">
            <Input value={form.full_name} onChange={set('full_name')} placeholder="Jane Smith" autoFocus/>
          </Field>
          <Field label="Email address">
            <Input value={form.email} onChange={set('email')} type="email" placeholder="jane@company.com"/>
          </Field>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          <Field label="Password" hint="Min 8 chars, 1 uppercase, 1 digit">
            <Input value={form.password} onChange={set('password')} type="password" placeholder="Temporary password"/>
          </Field>
          <Field label="Organisation role">
            <Select value={form.role_code} onChange={set('role_code')}>
              {availableRoles.map(r => (
                <option key={r} value={r}>{ROLE_META[r].label}</option>
              ))}
            </Select>
          </Field>
        </div>

        {/* Role description */}
        {selectedRole && (
          <div style={{ padding:'10px 12px', background:'#F1F5FB',
            border:'1px solid var(--bd)', borderRadius:4, marginBottom:20,
            display:'flex', alignItems:'center', gap:10 }}>
            <RolePill code={form.role_code}/>
            <span style={{ fontSize:12, color:'#64748B' }}>{selectedRole.desc}</span>
          </div>
        )}

        <div style={{ padding:'8px 12px', background:'rgba(251,191,36,.07)',
          border:'1px solid rgba(251,191,36,.2)', borderRadius:4, marginBottom:20,
          fontSize:11, color:'#fbbf24' }}>
          User will be prompted to change their password on first login.
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? <><Spinner size={11}/>&nbsp;Creating…</> : 'Create User'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Edit User Modal ───────────────────────────────────────────────────────────
function EditUserModal({ user: u, onClose, onSaved, currentUserRole }) {
  const [roleCode, setRoleCode] = useState(u.role_code);
  const [isActive, setIsActive] = useState(u.is_active);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const availableRoles = currentUserRole === 'ADMIN'
    ? ['ADMIN','ORG_ADMIN','ML_ENGINEER','DATA_ENGINEER','VIEWER']
    : ['ORG_ADMIN','ML_ENGINEER','DATA_ENGINEER','VIEWER'];

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await apiFetch(`/admin/users/${u.id}`, {
        method:'PATCH',
        body: JSON.stringify({ role_code: roleCode, is_active: isActive }),
      });
      onSaved(); onClose();
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Edit User" onClose={onClose}>
      <div style={{ width:420 }}>
        <Err msg={err}/>

        {/* User identity - read only */}
        <div style={{ padding:'12px 14px', background:'#F1F5FB',
          border:'1px solid var(--bd)', borderRadius:4, marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#0F172A', marginBottom:2 }}>{u.full_name}</div>
          <div style={{ fontSize:12, color:'#64748B' }}>{u.email}</div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
          <Field label="Organisation role">
            <Select value={roleCode} onChange={e => setRoleCode(e.target.value)}>
              {availableRoles.map(r => (
                <option key={r} value={r}>{ROLE_META[r].label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={String(isActive)} onChange={e => setIsActive(e.target.value === 'true')}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>
          </Field>
        </div>

        {ROLE_META[roleCode] && (
          <div style={{ padding:'8px 12px', background:'#F1F5FB',
            border:'1px solid var(--bd)', borderRadius:4, marginBottom:20,
            display:'flex', gap:10, alignItems:'center' }}>
            <RolePill code={roleCode}/>
            <span style={{ fontSize:11, color:'#64748B' }}>{ROLE_META[roleCode].desc}</span>
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? <><Spinner size={11}/>&nbsp;Saving…</> : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Add Member Modal ──────────────────────────────────────────────────────────
function AddMemberModal({ project, users, onClose, onAdded }) {
  const [userId, setUserId] = useState('');
  const [role,   setRole]   = useState('ML_ENGINEER');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);

  const save = async () => {
    if (!userId) { setErr('Select a user to add.'); return; }
    setSaving(true); setErr(null);
    try {
      await apiFetch(`/admin/projects/${project.id}/members`, {
        method:'POST', body: JSON.stringify({ user_id: userId, role }),
      });
      onAdded(); onClose();
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const selectedMeta = PROJECT_ROLE_META[role];

  return (
    <Modal title={`Add Member - ${project.project_name}`} onClose={onClose}>
      <div style={{ width:440 }}>
        <Err msg={err}/>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          <Field label="User">
            <Select value={userId} onChange={e => setUserId(e.target.value)}>
              <option value="">- Select user -</option>
              {(users||[]).map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Project role">
            <Select value={role} onChange={e => setRole(e.target.value)}>
              {Object.entries(PROJECT_ROLE_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </Select>
          </Field>
        </div>

        {selectedMeta && (
          <div style={{ padding:'8px 12px', background:'#F1F5FB',
            border:'1px solid var(--bd)', borderRadius:4, marginBottom:20,
            display:'flex', gap:10, alignItems:'center' }}>
            <RolePill code={role}/>
            <span style={{ fontSize:11, color:'#64748B' }}>{selectedMeta.desc}</span>
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !userId}>
            {saving ? <><Spinner size={11}/>&nbsp;Adding…</> : 'Add Member'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ users, currentUser, currentRole }) {
  const counts = {};
  (users || []).forEach(u => { counts[u.role_code] = (counts[u.role_code] || 0) + 1; });

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>

      {/* Hierarchy diagram */}
      <div style={{ background:'#F1F5FB', border:'1px solid var(--bd)', borderRadius:6, padding:24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ fontSize:12, fontWeight:700, color:'#0F172A' }}>Access Hierarchy</div>
        {currentUser?.role && (
          <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#64748B' }}>
            You are signed in as <RolePill code={currentUser.role}/>
          </div>
        )}
      </div>
        {[
          { role:'ADMIN',     indent:0, note:'Manages all organisations and platform settings.' },
          { role:'ORG_ADMIN', indent:1, note:'Manages users and projects within this organisation.' },
          { role:'ML_ENGINEER',   indent:2, note:'Trains models and starts runs within assigned projects.' },
          { role:'DATA_ENGINEER',  indent:2, note:'Manages data and feature configs within assigned projects.' },
          { role:'VIEWER',    indent:2, note:'Read-only access to projects they are members of.' },
        ].map(({ role, indent, note }) => (
          <div key={role} style={{ display:'flex', alignItems:'flex-start', gap:10,
            marginBottom:12, paddingLeft: indent * 20 }}>
            {indent > 0 && <span style={{ color:'#C8D5E8', fontSize:11, marginTop:2, flexShrink:0 }}>└</span>}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                <RolePill code={role}/>
                <span style={{ fontSize:10, fontFamily:'var(--f-mono)', color:'#64748B' }}>
                  {counts[role] || 0} user{counts[role] !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ fontSize:11, color:'#64748B', lineHeight:1.5 }}>{note}</div>
            </div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div style={{ background:'#F1F5FB', border:'1px solid var(--bd)', borderRadius:6, padding:24 }}>
        <div style={{ fontSize:12, fontWeight:700, color:'#0F172A', marginBottom:12 }}>How Access Works</div>
        {currentRole === 'ORG_ADMIN' && (
          <div style={{ padding:'8px 12px', background:'rgba(251,191,36,.07)',
            border:'1px solid rgba(251,191,36,.2)', borderRadius:4, marginBottom:14,
            fontSize:11, color:'#fbbf24', lineHeight:1.5 }}>
            You are an <strong>Org Admin</strong>. You can manage users and project access
            within this organisation. You cannot create Platform Admin accounts.
          </div>
        )}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {[
            { step:'1', title:'Users are created in the organisation',
              body:'Every user belongs to this organisation. Platform Admins and Org Admins can create users and assign their org-level role.' },
            { step:'2', title:'Platform Admin and Org Admin have full access',
              body:'These roles can see and act on all projects without needing to be added as members.' },
            { step:'3', title:'Engineers and Viewers need project membership',
              body:'Add them to specific projects from the Project Access tab. Assign ML Engineer for model training access, Data Engineer for data pipeline access, or Viewer for read-only access.' },
          ].map(({ step, title, body }) => (
            <div key={step} style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
              <div style={{ width:22, height:22, borderRadius:'50%', flexShrink:0,
                background:'rgba(232,71,10,.15)', border:'1px solid rgba(232,71,10,.3)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:10, fontWeight:800, color:'var(--or)', fontFamily:'var(--f-mono)' }}>
                {step}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#0F172A', marginBottom:3 }}>{title}</div>
                <div style={{ fontSize:11, color:'#64748B', lineHeight:1.6 }}>{body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────
function UsersTab({ users, loading, currentUser, currentRole, onReload }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editUser,   setEditUser]   = useState(null);

  const deactivate = async (userId) => {
    if (!window.confirm('Deactivate this user? They will no longer be able to log in.')) return;
    try {
      await apiFetch(`/admin/users/${userId}`, { method:'DELETE' });
      onReload();
    } catch(e) { alert(e.message); }
  };

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:12, color:'#64748B' }}>
          {users ? `${users.length} user${users.length !== 1 ? 's' : ''} in this organisation` : ''}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          + Add User
        </button>
      </div>

      <div style={{ background:'#F1F5FB', border:'1px solid var(--bd)', borderRadius:6, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#E4EAF4', borderBottom:'1px solid var(--bd)' }}>
              {['Name','Email','Org Role','Status','Last login',''].map(h => (
                <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:10,
                  fontWeight:700, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em',
                  whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ padding:32, textAlign:'center' }}><Spinner/></td></tr>
            )}
            {!loading && users && users.length === 0 && (
              <tr><td colSpan={6} style={{ padding:32, textAlign:'center', fontSize:12, color:'#64748B' }}>
                No users yet.
              </td></tr>
            )}
            {(users || []).map(u => (
              <tr key={u.id} style={{ borderBottom:'1px solid var(--bd)',
                opacity: u.is_active ? 1 : .5 }}>
                <td style={{ padding:'11px 16px' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0F172A', display:'flex', alignItems:'center', gap:6 }}>
                    {u.full_name}
                    {u.id === currentUser?.user_id && (
                      <span style={{ fontSize:9, fontFamily:'var(--f-mono)', color:'var(--or)', fontWeight:700 }}>YOU</span>
                    )}
                  </div>
                </td>
                <td style={{ padding:'11px 16px', fontSize:12, color:'#334155' }}>{u.email}</td>
                <td style={{ padding:'11px 16px' }}><RolePill code={u.role_code}/></td>
                <td style={{ padding:'11px 16px' }}>
                  <span style={{ fontSize:11, fontFamily:'var(--f-mono)', fontWeight:600,
                    color: u.is_active ? '#34d399' : '#f87171' }}>
                    {u.is_active ? '● active' : '○ inactive'}
                  </span>
                </td>
                <td style={{ padding:'11px 16px', fontSize:11, color:'#64748B', fontFamily:'var(--f-mono)' }}>
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding:'11px 16px' }}>
                  {u.id !== currentUser?.user_id && (
                    <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                      <button className="btn btn-ghost btn-xs"
                        onClick={() => setEditUser(u)}>Edit</button>
                      {u.is_active && (
                        <button className="btn btn-ghost btn-xs"
                          style={{ color:'#f87171' }}
                          onClick={() => deactivate(u.id)}>Deactivate</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateUserModal
          currentUserRole={currentRole}
          onClose={() => setShowCreate(false)}
          onCreated={onReload}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          currentUserRole={currentRole}
          onClose={() => setEditUser(null)}
          onSaved={onReload}
        />
      )}
    </>
  );
}

// ── Project Access Tab ────────────────────────────────────────────────────────
function ProjectAccessTab({ users }) {
  const { data: projects } = useApi('/projects?page_size=100');
  const projectList = projects?.items || (Array.isArray(projects) ? projects : []);

  return (
    <>
      <div style={{ padding:'10px 14px', background:'rgba(59,130,246,.07)',
        border:'1px solid rgba(59,130,246,.2)', borderRadius:4, marginBottom:20,
        fontSize:12, color:'#334155', lineHeight:1.6 }}>
        <strong style={{ color:'#60a5fa' }}>Platform Admin and Org Admin</strong> users have access
        to all projects automatically - they don't need to be added here.
        Use this tab to control which projects <strong>Engineers</strong> and <strong>Viewers</strong> can access.
      </div>

      {projectList.length === 0 && (
        <div style={{ textAlign:'center', padding:40, color:'#64748B', fontSize:13 }}>
          No projects yet. Create a project first.
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {projectList.map(p => (
          <ProjectMembersCard key={p.id} project={p} users={users || []}/>
        ))}
      </div>
    </>
  );
}

function ProjectMembersCard({ project, users }) {
  const { data: members, reload } = useApi(`/admin/projects/${project.id}/members`);
  const [showAdd,   setShowAdd]   = useState(false);
  const [removing,  setRemoving]  = useState(null);

  const remove = async (userId) => {
    setRemoving(userId);
    try {
      await apiFetch(`/admin/projects/${project.id}/members/${userId}`, { method:'DELETE' });
      reload();
    } catch(e) { alert(e.message); }
    finally { setRemoving(null); }
  };

  const activeMembers = (members || []).filter(m => m.is_active);

  return (
    <div style={{ background:'#F1F5FB', border:'1px solid var(--bd)', borderRadius:6, overflow:'hidden' }}>
      {/* Card header */}
      <div style={{ padding:'12px 18px', borderBottom: activeMembers.length ? '1px solid var(--bd)' : 'none',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        background:'#E4EAF4' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'#0F172A' }}>{project.project_name}</div>
            {project.client_name && (
              <div style={{ fontSize:11, color:'#64748B' }}>{project.client_name}</div>
            )}
          </div>
          <span style={{ fontSize:10, fontFamily:'var(--f-mono)', color:'#64748B',
            background:'#F1F5FB', padding:'2px 8px', borderRadius:3,
            border:'1px solid var(--bd)' }}>
            {activeMembers.length} member{activeMembers.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button className="btn btn-ghost btn-xs" onClick={() => setShowAdd(true)}>
          + Add Member
        </button>
      </div>

      {/* Members */}
      {!members && <div style={{ padding:'12px 18px' }}><Spinner size={14}/></div>}
      {members && activeMembers.length === 0 && (
        <div style={{ padding:'12px 18px', fontSize:12, color:'#64748B' }}>
          No explicit members - only Platform Admins and Org Admins can access this project.
        </div>
      )}
      {activeMembers.map((m, i) => (
        <div key={m.user_id} style={{
          padding:'10px 18px',
          borderBottom: i < activeMembers.length - 1 ? '1px solid var(--bd)' : 'none',
          display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#0F172A' }}>{m.full_name}</div>
            <div style={{ fontSize:11, color:'#64748B' }}>{m.email}</div>
          </div>
          <RolePill code={m.role}/>
          <button className="btn btn-ghost btn-xs"
            disabled={removing === m.user_id}
            onClick={() => remove(m.user_id)}
            style={{ color:'#f87171', flexShrink:0 }}>
            {removing === m.user_id ? <Spinner size={10}/> : 'Remove'}
          </button>
        </div>
      ))}

      {showAdd && (
        <AddMemberModal
          project={project}
          users={users}
          onClose={() => setShowAdd(false)}
          onAdded={reload}
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

// ── Job Queue Tab ─────────────────────────────────────────────────────────────
function QueueTab() {
  const { data, loading, reload } = useApi('/admin/queue');
  const [cancelling, setCancelling] = useState({});
  const [promoting,  setPromoting]  = useState({});
  const [err,        setErr]        = useState(null);

  const queue       = data?.queue || [];
  const servers     = data?.server_utilisation || [];
  const summary     = data?.summary || {};

  const cancel = async (runId, jobId) => {
    if (!window.confirm('Cancel this run? The job will be killed on the remote server.')) return;
    setCancelling(s => ({...s, [jobId]: true})); setErr(null);
    try {
      await apiFetch(`/admin/runs/${runId}/cancel`, {
        method: 'POST', body: JSON.stringify({ reason: 'Cancelled by admin' })
      });
      reload();
    } catch(e) { setErr(e.message); }
    finally { setCancelling(s => ({...s, [jobId]: false})); }
  };

  const setPriority = async (runId, priority) => {
    setPromoting(s => ({...s, [runId]: true})); setErr(null);
    try {
      await apiFetch(`/admin/runs/${runId}/priority`, {
        method: 'POST', body: JSON.stringify({ priority })
      });
      reload();
    } catch(e) { setErr(e.message); }
    finally { setPromoting(s => ({...s, [runId]: false})); }
  };

  if (loading) return <div style={{ padding:40, textAlign:'center' }}><Spinner /></div>;

  const statusColor = { RUNNING: '#16a34a', QUEUED: '#2563eb', WAITING_FOR_RESOURCES: '#d97706' };
  const statusLabel = { RUNNING: 'Running', QUEUED: 'Queued', WAITING_FOR_RESOURCES: 'Waiting' };

  return (
    <div>
      {err && (
        <div style={{ background:'rgba(220,38,38,.1)', border:'1px solid rgba(220,38,38,.3)',
          borderRadius:6, padding:'10px 14px', fontSize:12, color:'#dc2626', marginBottom:16 }}>
          {err}
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display:'flex', gap:12, marginBottom:24 }}>
        {[
          { label:'Running',  count: summary.running || 0, color:'#16a34a' },
          { label:'Queued',   count: summary.queued  || 0, color:'#2563eb' },
          { label:'Waiting',  count: summary.waiting || 0, color:'#d97706' },
        ].map(({ label, count, color }) => (
          <div key={label} style={{ flex:1, padding:'14px 18px', background:'#E4EAF4',
            border:'1px solid var(--bd)', borderRadius:8, borderLeft:`3px solid ${color}` }}>
            <div style={{ fontSize:11, color:'#64748B', textTransform:'uppercase',
              letterSpacing:'.07em', fontWeight:600, marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:24, fontWeight:700, color }}>{count}</div>
          </div>
        ))}
        <button onClick={reload}
          style={{ padding:'0 16px', background:'#E4EAF4', border:'1px solid var(--bd)',
            borderRadius:8, cursor:'pointer', fontSize:12, color:'#64748B' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Server utilisation */}
      {servers.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
            letterSpacing:'.07em', color:'#64748B', marginBottom:10 }}>Server Utilisation</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {servers.map(s => {
              const ramPct = s.ram_gb ? Math.round((s.used_ram / s.ram_gb) * 100) : 0;
              return (
                <div key={s.id} style={{ padding:'12px 16px', background:'#E4EAF4',
                  border:'1px solid var(--bd)', borderRadius:6, minWidth:180 }}>
                  <div style={{ fontWeight:600, fontSize:13, marginBottom:6 }}>{s.server_name}</div>
                  <div style={{ fontSize:11, color:'#64748B', marginBottom:6 }}>{s.host}</div>
                  <div style={{ display:'flex', gap:8, fontSize:11, color:'#334155' }}>
                    <span>RAM {s.used_ram?.toFixed(0)||0}/{s.ram_gb}GB</span>
                    <span>·</span>
                    <span>{s.active_jobs} job{s.active_jobs !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ marginTop:8, height:4, background:'#C8D5E8', borderRadius:2 }}>
                    <div style={{ height:'100%', borderRadius:2, width:`${Math.min(ramPct,100)}%`,
                      background: ramPct > 80 ? '#dc2626' : ramPct > 60 ? '#d97706' : '#16a34a' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Job queue table */}
      {queue.length === 0 ? (
        <div style={{ textAlign:'center', color:'#64748B', fontSize:13, padding:'40px 0' }}>
          No active jobs
        </div>
      ) : (
        <div className="card" style={{ overflow:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--bd)', background:'#E4EAF4' }}>
                {['Status','Project','Family','Version','Server','Wait / Running','Retry','Actions'].map(h => (
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700,
                    fontSize:10, textTransform:'uppercase', letterSpacing:'.06em',
                    color:'#64748B', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {queue.map((job, i) => (
                <tr key={job.job_id} style={{ borderBottom:'1px solid var(--bd)',
                  background: i % 2 === 0 ? 'transparent' : '#E4EAF4' }}>
                  <td style={{ padding:'9px 12px', whiteSpace:'nowrap' }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:10,
                      background: (statusColor[job.status] || '#888') + '22',
                      color: statusColor[job.status] || '#64748B' }}>
                      {statusLabel[job.status] || job.status}
                    </span>
                  </td>
                  <td style={{ padding:'9px 12px' }}>
                    <div style={{ fontWeight:600 }}>{job.project}</div>
                    {job.client && <div style={{ fontSize:11, color:'#64748B' }}>{job.client}</div>}
                  </td>
                  <td style={{ padding:'9px 12px', color:'#334155', fontFamily:'var(--f-mono)', fontSize:11 }}>
                    {job.family}
                  </td>
                  <td style={{ padding:'9px 12px', fontFamily:'var(--f-mono)', fontSize:11 }}>
                    v{job.version}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:11, color:'#64748B' }}>
                    {job.server}
                  </td>
                  <td style={{ padding:'9px 12px', fontFamily:'var(--f-mono)', fontSize:11,
                    color: job.status === 'WAITING_FOR_RESOURCES' ? '#d97706' : '#334155' }}>
                    {job.status === 'RUNNING'
                      ? job.running_duration
                      : job.status === 'WAITING_FOR_RESOURCES'
                        ? `⏳ ${job.wait_duration}`
                        : job.wait_duration || '-'}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:11, color:'#64748B', textAlign:'center' }}>
                    {job.retry_count > 0
                      ? <span style={{ color:'#d97706', fontWeight:600 }}>{job.retry_count}</span>
                      : '-'}
                  </td>
                  <td style={{ padding:'9px 12px', whiteSpace:'nowrap' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      {job.status === 'WAITING_FOR_RESOURCES' && (
                        <button
                          className="btn btn-ghost btn-xs"
                          disabled={promoting[job.run_id]}
                          onClick={() => setPriority(job.run_id, 'HIGH')}
                          title="Boost to HIGH priority">
                          ↑ Boost
                        </button>
                      )}
                      <button
                        className="btn btn-err btn-xs"
                        disabled={cancelling[job.job_id]}
                        onClick={() => cancel(job.run_id, job.job_id)}>
                        {cancelling[job.job_id] ? <Spinner size={10}/> : '✕ Cancel'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ── Audit Log Tab ─────────────────────────────────────────────────────────────
function AuditTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const PAGE_SIZE = 50;

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const data = await apiFetch(`/admin/audit?page=${p}&page_size=${PAGE_SIZE}`);
      setEntries(data?.entries || []);
      setPage(p);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(1); }, []);

  const ACTION_COLOUR = {
    LOGIN: '#2563EB', LOGOUT: '#64748B',
    RECORD_CREATED: '#059669', RECORD_UPDATED: '#D97706',
    RECORD_DELETED: '#DC2626', JOB_DISPATCHED: '#7C3AED',
    LOGIN_FAILED: '#DC2626',
  };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:600, color:'#0D1B2E' }}>Audit Log</div>
          <div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>
            Every create, update, delete, login and job dispatch — automatically logged.
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => load(1)}>↺ Refresh</button>
      </div>

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'#94A3B8' }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ padding:40, textAlign:'center', color:'#94A3B8' }}>No audit entries found.</div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>TIME</th>
                <th>ACTION</th>
                <th>RESOURCE</th>
                <th>USER</th>
                <th>IP</th>
                <th>DETAIL</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i}>
                  <td className="td-mono" style={{ whiteSpace:'nowrap', fontSize:11 }}>
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td>
                    <span style={{
                      display:'inline-block', padding:'2px 8px',
                      borderRadius:99, fontSize:10, fontWeight:700,
                      background: (ACTION_COLOUR[e.action] || '#64748B') + '15',
                      color: ACTION_COLOUR[e.action] || '#64748B',
                      border: `1px solid ${(ACTION_COLOUR[e.action] || '#64748B')}30`,
                      letterSpacing:'.05em',
                    }}>{e.action}</span>
                  </td>
                  <td style={{ fontSize:12 }}>
                    <div style={{ fontWeight:500, color:'#0D1B2E' }}>{e.resource_type}</div>
                    {e.resource_id && (
                      <div className="td-mono" style={{ fontSize:10, color:'#94A3B8' }}>
                        {e.resource_id.slice(0,8)}…
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize:12 }}>
                    <div style={{ color:'#0D1B2E' }}>{e.user_email || e.user_id?.slice(0,8)}</div>
                  </td>
                  <td className="td-mono" style={{ fontSize:11, color:'#94A3B8' }}>{e.ip_address || '—'}</td>
                  <td style={{ fontSize:11, color:'#64748B', maxWidth:240 }}>
                    {e.new_value ? (
                      <span title={JSON.stringify(e.new_value)}>
                        {Object.entries(e.new_value).slice(0,2).map(([k,v]) =>
                          `${k}: ${String(v).slice(0,30)}`
                        ).join(' · ')}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display:'flex', gap:8, marginTop:16, justifyContent:'flex-end' }}>
        {page > 1 && (
          <button className="btn btn-ghost btn-sm" onClick={() => load(page - 1)}>← Prev</button>
        )}
        <span style={{ fontSize:12, color:'#94A3B8', lineHeight:'28px' }}>Page {page}</span>
        {entries.length === PAGE_SIZE && (
          <button className="btn btn-ghost btn-sm" onClick={() => load(page + 1)}>Next →</button>
        )}
      </div>
    </div>
  );
}

export default function AdminPage({ user: currentUser }) {
  const { data: users, loading, reload } = useApi('/admin/users');
  const [tab, setTab] = useState('overview');
  const currentRole = currentUser?.role || '';

  const TABS = [
    { key:'overview', label:'Overview' },
    { key:'users',    label:`Users${users ? ` (${users.length})` : ''}`, tourKey:'admin-users' },
    { key:'access',   label:'Project Access' },
    { key:'queue',    label:'Job Queue' },
    { key:'audit',    label:'Audit Log', tourKey:'admin-audit' },
  ];

  return (
    <div className="page">
      <div style={{ maxWidth:960 }}>

        {/* Page sub-header */}
        <div style={{ marginBottom:24 }}>
          <p style={{ fontSize:12, color:'#64748B', margin:0 }}>
            User management and project access control for this organisation.
          </p>
        </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--bd)', marginBottom:24 }}>
        {TABS.map(({ key, label, tourKey }) => (
          <button key={key} onClick={() => setTab(key)} data-tour={tourKey} style={{
            padding:'9px 18px', border:'none', background:'transparent', cursor:'pointer',
            fontSize:12, fontWeight:600, letterSpacing:'.01em',
            borderBottom: tab===key ? '2px solid var(--or)' : '2px solid transparent',
            color: tab===key ? 'var(--or)' : '#64748B',
            marginBottom:-1, transition:'color .15s',
          }}>{label}</button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab users={users} currentUser={currentUser} currentRole={currentRole}/>}
      {tab === 'users'    && (
        <UsersTab
          users={users} loading={loading}
          currentUser={currentUser} currentRole={currentRole}
          onReload={reload}
        />
      )}
      {tab === 'access'   && <ProjectAccessTab users={users}/>}
      {tab === 'queue'    && <QueueTab />}
      {tab === 'audit'    && <AuditTab />}
      </div>
    </div>
  );
}